import { useEffect, useRef } from 'react';

import { getSessionExpiryMs } from 'helpers/session';
import { useDispatch } from 'react-redux';

import { authenticate } from 'api/authentication-slice';

const WARNING_WINDOW_MS = Number(process.env.REACT_APP_SESSION_TIMEOUT_WARNING_MS) || 5 * 60 * 1000;
const REFRESH_THRESHOLD_MS = WARNING_WINDOW_MS * 2;
const CHECK_INTERVAL_MS = 1000;
const ACTIVE_RECENTLY_MS = CHECK_INTERVAL_MS * 2;

const MIN_REFRESH_INTERVAL_MS = 30 * 1000;
const REFRESH_TIMEOUT_MS = 10 * 1000;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'scroll'];

const POINTER_ACTIVITY_EVENTS = ['mousedown', 'touchstart', 'click'];
const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"], [tabindex]';

const isInteractiveTarget = target => {
  const el = target instanceof Element ? target : target?.parentElement;
  return !!el?.closest?.(INTERACTIVE_SELECTOR);
};

export const useSessionTimeout = ({ enabled, onWarning, onWarningCleared, onExpired }) => {
  const dispatch = useDispatch();

  const lastActivityRef = useRef(Date.now());
  const warningRef = useRef(false);
  const expiredRef = useRef(false);
  const pendingRefreshRef = useRef(false);
  const lastRefreshAttemptRef = useRef(0);

  const refreshSession = () => {
    pendingRefreshRef.current = true;
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(
        () => reject(new Error('Session refresh timed out')),
        REFRESH_TIMEOUT_MS,
      );
      dispatch(authenticate())
        .unwrap()
        .then(resolve, reject)
        .finally(() => clearTimeout(timeoutId));
    }).finally(() => {
      pendingRefreshRef.current = false;
    });
  };

  const resetAfterStaySignedIn = () => {
    warningRef.current = false;
    expiredRef.current = false;
  };

  useEffect(() => {
    if (!enabled) return;

    const registerActivity = () => {
      if (warningRef.current) return;
      lastActivityRef.current = Date.now();
    };

    const registerPointerActivity = event => {
      if (warningRef.current) return;
      if (!isInteractiveTarget(event.target)) return;
      lastActivityRef.current = Date.now();
    };

    ACTIVITY_EVENTS.forEach(eventName =>
      document.addEventListener(eventName, registerActivity, { passive: true }),
    );
    POINTER_ACTIVITY_EVENTS.forEach(eventName =>
      document.addEventListener(eventName, registerPointerActivity, { passive: true }),
    );

    const tick = () => {
      // A refresh is in progress, wait for it to complete before re-evaluating expiry
      if (pendingRefreshRef.current) return;

      const expiryMs = getSessionExpiryMs();

      if (!expiryMs) {
        if (!expiredRef.current) {
          expiredRef.current = true;
          onExpired();
        }
        return;
      }

      const msRemaining = expiryMs - Date.now();

      if (msRemaining <= 0) {
        if (!expiredRef.current) {
          expiredRef.current = true;
          onExpired();
        }
        return;
      }

      const recentlyActive = Date.now() - lastActivityRef.current < ACTIVE_RECENTLY_MS;

      const attemptThrottledRefresh = () => {
        const now = Date.now();
        if (now - lastRefreshAttemptRef.current < MIN_REFRESH_INTERVAL_MS) return;
        lastRefreshAttemptRef.current = now;
        refreshSession().catch(() => {});
      };

      // True warning zone — always surface the modal. A silent refresh here can fail
      if (msRemaining <= WARNING_WINDOW_MS) {
        warningRef.current = true;
        onWarning(msRemaining);
        // Still try a background refresh if the user is active — on success this
        // closes the modal again almost immediately via onWarningCleared next tick.
        if (recentlyActive) attemptThrottledRefresh();
        return;
      }

      // Not in the warning zone (anymore) — clear any previously-shown modal
      // regardless of which branch below we take next.
      if (warningRef.current) {
        warningRef.current = false;
        onWarningCleared();
      }

      // Outside the warning zone but close enough that an active user should never
      // reach it at all — keep the session alive silently, no modal needed yet.
      if (recentlyActive && msRemaining <= REFRESH_THRESHOLD_MS) {
        attemptThrottledRefresh();
      }
    };

    tick();
    const intervalId = setInterval(tick, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach(eventName =>
        document.removeEventListener(eventName, registerActivity),
      );
      POINTER_ACTIVITY_EVENTS.forEach(eventName =>
        document.removeEventListener(eventName, registerPointerActivity),
      );
    };
  }, [enabled]);

  return { resetAfterStaySignedIn, refreshSession };
};
